import { emit, listen, type UnlistenFn } from "@tauri-apps/api/event";
import {
  settingsPageDef,
  type SettingsTarget,
} from "@/components/settings/settings-navigation";
import { isTauri } from "@/lib/is-tauri";
import {
  getDesktopChatAuthScope,
  isCurrentDesktopChatAuthScope,
} from "./desktop-chat-auth-scope";
import { getWindowDisplayId } from "./use-window-role";
import { windowOpenerFrom, type WindowOpener } from "./window-opener";

export const SETTINGS_DISPLAY_OPEN = "denker://settings-display-open";
type DisplayRequest = {
  displayId: number;
  target: SettingsTarget;
  opener: WindowOpener;
  authScope: unknown;
};

function validDisplay(value: unknown): value is number {
  return (
    typeof value === "number" &&
    Number.isSafeInteger(value) &&
    value >= 0 &&
    value <= 0xffffffff
  );
}

/** A Settings target from untrusted input: a defined page and string args only. */
export function settingsTargetFrom(value: unknown): SettingsTarget | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const target = value as SettingsTarget;
  if (typeof target.page !== "string" || !settingsPageDef(target.page))
    return null;
  if (
    target.args !== undefined &&
    (!target.args ||
      typeof target.args !== "object" ||
      Array.isArray(target.args) ||
      Object.keys(target.args).length > 16 ||
      Object.values(target.args).some(
        (arg) => typeof arg !== "string" || arg.length > 1000,
      ))
  )
    return null;
  // Navigation carries only page arguments; approval never crosses this channel.
  return { page: target.page, ...(target.args ? { args: target.args } : {}) };
}

/** Route to the clicked overlay, which owns its display's viewport and placement. */
export function routeSettingsRequestToDisplay(
  target: SettingsTarget,
  opener: WindowOpener,
  displayId?: number,
): boolean {
  if (
    !isTauri() ||
    displayId === undefined ||
    displayId === getWindowDisplayId()
  )
    return false;
  if (validDisplay(displayId)) {
    void emit(SETTINGS_DISPLAY_OPEN, {
      displayId,
      target,
      opener,
      authScope: getDesktopChatAuthScope(),
    } satisfies DisplayRequest).catch(() => {});
  }
  // A missing target display must not open Settings on a different display.
  return true;
}

/** Attached before the lazy app on every display. No browser setup runs here. */
export function listenForSettingsDisplayRequests(
  open: (target: SettingsTarget, opener: WindowOpener) => Promise<void>,
): Promise<UnlistenFn> {
  return listen<unknown>(SETTINGS_DISPLAY_OPEN, ({ payload }) => {
    if (!payload || typeof payload !== "object" || Array.isArray(payload))
      return;
    const request = payload as DisplayRequest;
    if (
      !validDisplay(request.displayId) ||
      request.displayId !== getWindowDisplayId() ||
      !isCurrentDesktopChatAuthScope(request.authScope)
    )
      return;
    const target = settingsTargetFrom(request.target);
    if (target)
      void open(target, windowOpenerFrom(request.opener)).catch(() => {});
  });
}
